import { useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  dismissDuplicateMutation,
  listDuplicatesOptions,
  listDuplicatesQueryKey,
  listMergesOptions,
  listMergesQueryKey,
  listWikiFilesQueryKey,
  mergePagesMutation,
  previewMergeMutation,
  undoMergeMutation,
} from "@/api";

export function useDuplicates(spaceUuid: string) {
  return useQuery({ ...listDuplicatesOptions({ path: { space_uuid: spaceUuid } }), select: (r) => r.data.pairs });
}

export function useMerges(spaceUuid: string) {
  return useQuery({ ...listMergesOptions({ path: { space_uuid: spaceUuid } }), select: (r) => r.data.merges });
}

/** Translate a merge error by its code; anything else falls back. */
export function useMergeErrorMessage() {
  const { t } = useTranslation("knowledge");
  return (error: unknown, fallback: string): string => {
    if (!(error instanceof ApiError)) return fallback;
    switch (error.code) {
      case "KG_MERGE_CONFLICT":
        return t("errors.KG_MERGE_CONFLICT");
      case "KG_MERGE_NOT_FOUND":
        return t("errors.KG_MERGE_NOT_FOUND");
      case "WIKI_FILE_NOT_FOUND":
        return t("errors.WIKI_FILE_NOT_FOUND");
      default:
        return error.detail ?? fallback;
    }
  };
}

/** After a merge or undo: pages, duplicates, history and any open page all changed. */
function useInvalidateSpace(spaceUuid: string) {
  const queryClient = useQueryClient();
  const path = { path: { space_uuid: spaceUuid } };
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: listDuplicatesQueryKey(path) }),
      queryClient.invalidateQueries({ queryKey: listMergesQueryKey(path) }),
      queryClient.invalidateQueries({ queryKey: listWikiFilesQueryKey(path) }),
      queryClient.invalidateQueries({
        predicate: (query) => (query.queryKey[0] as { _id?: string } | undefined)?._id === "getWikiFile",
      }),
    ]);
}

export function usePreviewMerge() {
  return useMutation({ ...previewMergeMutation() });
}

export function useMergePages(spaceUuid: string) {
  const invalidate = useInvalidateSpace(spaceUuid);
  return useMutation({ ...mergePagesMutation(), onSuccess: () => invalidate() });
}

export function useUndoMerge(spaceUuid: string) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const message = useMergeErrorMessage();
  const invalidate = useInvalidateSpace(spaceUuid);
  return useMutation({
    ...undoMergeMutation(),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("merge.undone"));
    },
    onError: (error) => toast.error(message(error, t("merge.undoFailed"))),
  });
}

export function useDismissDuplicate(spaceUuid: string) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const message = useMergeErrorMessage();
  const queryClient = useQueryClient();
  return useMutation({
    ...dismissDuplicateMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: listDuplicatesQueryKey({ path: { space_uuid: spaceUuid } }) }),
    onError: (error) => toast.error(message(error, t("duplicates.dismissFailed"))),
  });
}
